import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import apiService from '../services/api';

const SETTING_CODE = 'sale_tag_values';
const SETTING_NAME = 'Sale Tag Values';

const emptyItem = { name: '', code: '', is_active: false, ordinal: 0 };

const SaleTagValuesDefaults = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);
    const [items, setItems] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [itemForm, setItemForm] = useState(emptyItem);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getOrganizationDefaultSettingByCode(SETTING_CODE);
            if (response.success && response.data) {
                setSettingData(response.data);
                const list = Array.isArray(response.data.value) ? response.data.value : [];
                setItems(list.map(it => ({
                    name: it.name || '',
                    code: it.code || '',
                    is_active: it.is_active !== undefined ? Boolean(it.is_active) : false,
                    ordinal: Number(it.ordinal) || 0
                })));
            } else if (response.error && !String(response.error).toLowerCase().includes('not found')) {
                setError(response.error);
            }
        } catch (err) {
            console.error(`Error loading ${SETTING_CODE}:`, err);
            setError(`Failed to load ${SETTING_NAME}`);
        } finally {
            setLoading(false);
        }
    };

    const openAdd = () => {
        setEditingIndex(null);
        setItemForm({ ...emptyItem });
        setShowModal(true);
    };

    const openEdit = (index) => {
        const it = items[index];
        setEditingIndex(index);
        setItemForm({
            name: it.name,
            code: it.code,
            is_active: Boolean(it.is_active),
            ordinal: Number(it.ordinal) || 0
        });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setItemForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveItem = (e) => {
        e.preventDefault();
        const item = {
            name: (itemForm.name || '').trim(),
            code: (itemForm.code || '').trim(),
            is_active: Boolean(itemForm.is_active),
            ordinal: Number(itemForm.ordinal) || 0
        };
        if (!item.name) return;

        setItems(prev => {
            const list = [...prev];
            if (editingIndex === null) list.push(item);
            else list[editingIndex] = item;
            return list;
        });
        setShowModal(false);
    };

    const handleDeleteItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const updateData = {
                name: SETTING_NAME,
                code: SETTING_CODE,
                value: items
            };

            const response = settingData && settingData.id
                ? await apiService.updateOrganizationDefaultSetting(settingData.id, updateData)
                : await apiService.createOrganizationDefaultSetting(updateData);

            if (response.success) {
                setSuccess(`${SETTING_NAME} saved successfully!`);
                setSettingData(response.data);
            } else {
                setError(response.error || `Failed to save ${SETTING_NAME}`);
            }
        } catch (err) {
            console.error(`Error saving ${SETTING_CODE}:`, err);
            setError(`Failed to save ${SETTING_NAME}`);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading {SETTING_NAME}...</div>
            </div>
        );
    }

    const sortedItems = [...items]
        .map((it, idx) => ({ ...it, _idx: idx }))
        .sort((a, b) => (a.ordinal - b.ordinal) || a.name.localeCompare(b.name));

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-tags me-2"></i>
                            {SETTING_NAME}
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3"><Col>
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </Alert>
                </Col></Row>
            )}
            {success && (
                <Row className="mb-3"><Col>
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="bi bi-check-circle me-2"></i>{success}
                    </Alert>
                </Col></Row>
            )}

            <Form onSubmit={handleSave}>
                <Card className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="bi bi-list-ul me-2"></i>
                            Tags
                        </h5>
                        <Button variant="primary" size="sm" onClick={openAdd}>
                            <i className="bi bi-plus-lg me-1"></i> Add Tag
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {sortedItems.length === 0 ? (
                            <div className="text-muted text-center py-3">No tags defined.</div>
                        ) : (
                            <Table striped hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>Ordinal</th>
                                        <th>Name</th>
                                        <th>Code</th>
                                        <th>Active</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.map(it => (
                                        <tr key={it._idx}>
                                            <td>{it.ordinal}</td>
                                            <td>{it.name}</td>
                                            <td><code>{it.code}</code></td>
                                            <td>
                                                {it.is_active
                                                    ? <span className="badge bg-success">Yes</span>
                                                    : <span className="badge bg-secondary">No</span>}
                                            </td>
                                            <td className="text-end">
                                                <Button size="sm" variant="outline-primary" className="me-2"
                                                    onClick={() => openEdit(it._idx)}>
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button size="sm" variant="outline-danger"
                                                    onClick={() => handleDeleteItem(it._idx)}>
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-end">
                    <Button type="submit" variant="success" disabled={saving}>
                        {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Saving...</>)
                            : (<><i className="bi bi-save me-2"></i>Save Changes</>)}
                    </Button>
                </div>
            </Form>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingIndex === null ? 'Add' : 'Edit'} Sale Tag Value</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveItem}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" name="name" value={itemForm.name}
                                onChange={handleFormChange} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Code</Form.Label>
                            <Form.Control type="text" name="code" value={itemForm.code}
                                onChange={handleFormChange} placeholder="e.g. _LIPA_NA_MPESA_" />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ordinal</Form.Label>
                                    <Form.Control type="number" min="0" step="1" name="ordinal"
                                        value={itemForm.ordinal} onChange={handleFormChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                                <Form.Check type="checkbox" name="is_active" id="saleTagValueActive"
                                    label="Active" checked={!!itemForm.is_active} onChange={handleFormChange} />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default SaleTagValuesDefaults;
